import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError, ruleReasons } from "../lib/api";
import { useUsers } from "../lib/hooks";
import { ReasonsList } from "./common";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";

export function useProjectPicker(param = "project", fallback = "prj_marina") {
  const [sp, setSp] = useSearchParams();
  const pid = sp.get(param) || fallback;
  const { data: projects } = useQuery({
    queryKey: ["ops-projects"],
    queryFn: async () => (await api.get("/proyectos")).data,
  });
  const picker = (
    <Select value={pid} onValueChange={(v) => setSp((prev) => { prev.set(param, v); return prev; })}>
      <SelectTrigger className="w-64" data-testid="ops-project-picker"><SelectValue /></SelectTrigger>
      <SelectContent>
        {(projects || []).map((p) => (
          <SelectItem key={p.id} value={p.id} data-testid={`ops-project-option-${p.id}`}>{p.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
  return [pid, picker, setSp];
}

const FIELD_META = {
  comment: { label: "Comentario", type: "textarea" },
  motivo: { label: "Motivo", type: "textarea" },
  analysis: { label: "Análisis", type: "textarea" },
  action_plan: { label: "Plan de acción", type: "textarea" },
  outcome: { label: "Resultado", type: "textarea" },
  evidence: { label: "Evidencia (documento, acta, referencia)", type: "textarea" },
  justificante: { label: "Justificante de presentación", type: "textarea" },
  resolution_doc: { label: "Documento de resolución", type: "textarea" },
  resolution_outcome: { label: "Resultado de la resolución", type: "select", options: ["aprobada", "denegada", "parcial"] },
  conditions: { label: "Condiciones", type: "textarea" },
  deadline: { label: "Fecha límite", type: "date" },
  due_date: { label: "Fecha compromiso", type: "date" },
  start_date: { label: "Fecha de inicio", type: "date" },
  expiry_date: { label: "Fecha de caducidad", type: "date" },
  amount: { label: "Importe (€)", type: "number" },
  supplier: { label: "Suministrador", type: "text" },
  document_ref: { label: "Referencia del documento", type: "text" },
  severity: { label: "Severidad", type: "select", options: ["menor", "mayor", "critica"] },
  documents: { label: "Documentos solicitados (separados por coma)", type: "text", toList: true },
  assignee_id: { label: "Responsable de la acción", type: "user" },
  responsible_id: { label: "Responsable", type: "user" },
  planning_impact: { label: "Impacto potencial en planning", type: "text" },
  confirm: { label: "Confirmo la operación", type: "bool" },
};

const ACTION_LABELS = {
  analizar: "Analizar", gestionar: "Registrar gestión", cerrar: "Cerrar", materializar: "Materializar en incidencia",
  evaluar: "Evaluar", aprobar: "Aprobar", rechazar: "Rechazar", implementar: "Implementada",
  realizar: "Marcar realizada", validar: "Validar", elevar_ncr: "Elevar a NCR", iniciar_accion: "Iniciar acción correctiva",
  aportar_evidencia: "Aportar evidencia", solicitar_verificacion: "Solicitar verificación", verificar_cierre: "Verificar y cerrar",
  reabrir: "Reabrir", descartar: "Descartar", confirmar: "Confirmar (humano)",
  documentar: "Documentación", revisar: "Revisar", presentar: "Presentar", tramitar: "En tramitación",
  recibir_requerimiento: "Registrar requerimiento", responder: "Responder", resolver: "Registrar resolución",
  condicionar: "Condiciones", recibir_oferta: "Registrar oferta", iniciar_ejecucion: "Iniciar ejecución",
  probar: "Registrar prueba", conectar: "Conectar", poner_en_servicio: "Poner en servicio",
  aportar: "Aportar documento", revisar_ok: "Validar documento",
  solicitar_actualizaciones: "Solicitar actualizaciones", generar_borrador: "Generar borrador IA",
  enviar_revision: "Enviar a revisión", solicitar_aprobacion: "Solicitar aprobación", emitir: "Emitir (snapshot)",
  abrir_preparacion: "Abrir preparación", convocar: "Convocar", iniciar: "Iniciar reunión",
  finalizar: "Finalizar (celebrada)", cancelar: "Cancelar reunión",
};

export function ActionButtons({ item, endpoint, invalidate, extraFields = {} }) {
  const qc = useQueryClient();
  const users = useUsers();
  const [open, setOpen] = useState(null);
  const [fields, setFields] = useState({});
  const [error, setError] = useState(null);
  const mut = useMutation({
    mutationFn: async ({ action, payload }) => (await api.post(`${endpoint}/${item.id}/accion`, { action, ...payload })).data,
    onSuccess: () => {
      toast.success("Acción aplicada");
      setOpen(null); setFields({}); setError(null);
      (invalidate || []).forEach((k) => qc.invalidateQueries({ queryKey: k }));
    },
    onError: (e) => setError(e),
  });
  const reasons = (a) => (a.reasons || []).map((r) => r.message || r.code);
  return (
    <div className="flex flex-wrap items-center gap-1">
      {(item.actions || []).filter((a) => a.allowed).map((a) => (
        <Button key={a.action} size="sm" variant="outline" data-testid={`act-${a.action}-${item.id}`}
          onClick={() => { setOpen(a); setFields({}); setError(null); }}>
          {ACTION_LABELS[a.action] || a.action}
        </Button>
      ))}
      {(item.actions || []).filter((a) => !a.allowed && (a.reasons || []).length).map((a) => (
        <span key={a.action} className="text-xs text-slate-400" title={reasons(a).join(" · ")}
          data-testid={`act-blocked-${a.action}-${item.id}`}>{ACTION_LABELS[a.action] || a.action} (bloqueada)</span>
      ))}
      <Dialog open={!!open} onOpenChange={() => setOpen(null)}>
        <DialogContent data-testid={`action-dialog-${open?.action || "none"}`}>
          <DialogHeader><DialogTitle>{ACTION_LABELS[open?.action] || open?.action} — {item.title || item.period}</DialogTitle></DialogHeader>
          {open && (
            <div className="space-y-3">
              {[...(open.needs || []), ...(extraFields[open.action] || [])].map((f) => {
                const meta = FIELD_META[f] || { label: f, type: "text" };
                return (
                  <div key={f}>
                    <Label>{meta.label} *</Label>
                    {meta.type === "textarea" && <Textarea value={fields[f] || ""} onChange={(e) => setFields({ ...fields, [f]: e.target.value })} data-testid={`field-${f}`} />}
                    {meta.type === "text" && <Input value={fields[f] || ""} onChange={(e) => setFields({ ...fields, [f]: e.target.value })} data-testid={`field-${f}`} />}
                    {meta.type === "number" && <Input type="number" value={fields[f] || ""} onChange={(e) => setFields({ ...fields, [f]: Number(e.target.value) })} data-testid={`field-${f}`} />}
                    {meta.type === "date" && <Input type="date" value={(fields[f] || "").slice(0, 10)} onChange={(e) => setFields({ ...fields, [f]: e.target.value })} data-testid={`field-${f}`} />}
                    {meta.type === "bool" && (
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!fields[f]} onChange={(e) => setFields({ ...fields, [f]: e.target.checked })} data-testid={`field-${f}`} />
                        {meta.label}
                      </label>
                    )}
                    {meta.type === "select" && (
                      <Select value={fields[f] || ""} onValueChange={(v) => setFields({ ...fields, [f]: v })}>
                        <SelectTrigger data-testid={`field-${f}`}><SelectValue placeholder="Selecciona" /></SelectTrigger>
                        <SelectContent>{meta.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                    {meta.type === "user" && (
                      <Select value={fields[f] || ""} onValueChange={(v) => setFields({ ...fields, [f]: v })}>
                        <SelectTrigger data-testid={`field-${f}`}><SelectValue placeholder="Selecciona usuario" /></SelectTrigger>
                        <SelectContent>{(users.users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </div>
                );
              })}
              {error && (ruleReasons(error)
                ? <ReasonsList reasons={ruleReasons(error)} />
                : <p className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700" data-testid="action-error">{apiError(error)}</p>)}
              <Button data-testid="action-submit" disabled={mut.isPending}
                onClick={() => {
                  const payload = { ...fields };
                  for (const f of [...(open.needs || []), ...(extraFields[open.action] || [])]) {
                    if (FIELD_META[f]?.toList && typeof payload[f] === "string")
                      payload[f] = payload[f].split(",").map((s) => s.trim()).filter(Boolean);
                    if (FIELD_META[f]?.type === "date" && typeof payload[f] === "string" && payload[f])
                      payload[f] = new Date(payload[f]).toISOString();
                  }
                  mut.mutate({ action: open.action, payload });
                }}>
                Confirmar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function useOpsMutation(invalidate) {
  const qc = useQueryClient();
  return useMutation({
    onSuccess: () => (invalidate || []).forEach((k) => qc.invalidateQueries({ queryKey: k })),
    onError: (e) => toast.error(apiError(e) || "Error"),
  });
}

export function OpsKpis({ pid }) {
  const { data } = useQuery({
    queryKey: ["ops-dashboard", pid],
    queryFn: async () => (await api.get(`/proyectos/${pid}/ops/dashboard`)).data,
  });
  if (!data) return null;
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5" data-testid="ops-kpis">
      {data.kpis.map((k) => (
        <a key={k.key} href={k.drill}
          className="ts-card block p-3 transition-all hover:border-blue-300 hover:shadow-sm" data-testid={`ops-kpi-${k.key}`}>
          <p className="ts-caption">{k.label}</p>
          <p className={`mt-0.5 font-mono text-lg font-bold ${k.alert ? "text-red-600" : "text-slate-900"}`}>{k.value}</p>
          <p className="mt-0.5 text-[11px] text-blue-700">Ver listado →</p>
        </a>
      ))}
    </div>
  );
}
